/**
 * Журнал ЕДО — ЦЕНТР ВІДПРАВКИ документів (рішення власника 2026-08-13):
 * ДВА НЕЗАЛЕЖНІ потоки у рядку, візуально розділені:
 *   МЕЙЛ:  [відправити мейл] → колонка «Статус мейла»;
 *   ЕЦП:   [підписати ЕЦП] (власний КЕП) → «✓ підписано» + активуються
 *          [на підпис] і [посилання] → «очікуємо підпису» → контрагент
 *          підписав → «підписано контрагентом» + [скачати] p7s.
 * Канон Норми #29 (2026-08-22): .card + InfiniteDocGrid (серверна
 * пагінація/сортування; фільтри — параметри запиту) + орг-фільтр.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, GridApi } from 'ag-grid-community';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { attachments } from '@/api/attachments';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { pickDefaultOrg, useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import KeyAuthPanel from '@/shared/eusign/KeyAuthPanel';
import { MethodWidget, SignMethodTabs, type SignMethod } from '@/shared/eusign/SignMethodTabs';
import EdoClaimPanel from '@/shared/components/EdoClaimPanel';
import { PENDING_CLAIM_KEY } from '@/api/edoSign';
import EdoBatchSignDialog from './EdoBatchSignDialog';
import {
  activateKey, activeKeyId, isSealOwner, keyCode, listKeys, loadEuSign,
  pickKeyForCode, signDataInternalCadesT, bytesToBase64, type StoredKey,
} from '@/shared/eusign/eusign';

interface EdoRow {
  id: string;
  organization_id: string;
  kind: string;
  sign_token?: string | null;
  status: string;
  sender_name: string | null;
  organization_code: string | null;
  sign_status: string | null;
  sign_email_sent: boolean;
  own_signed: boolean;
  signed_attachment_id: string | null;
  signer_info: string | null;
  document_type: string;
  document_id: string;
  attachment_id: string | null;
  recipient_email: string | null;
  subject: string | null;
  counterparty_id: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  counterparty_code: string | null;
  error_message: string | null;
  created_at: string;
  // 0428: внутрішня доставка користувачам системи.
  direction: 'OUT' | 'IN';
  peer_org_name: string | null;
  viewed_at: string | null;
}

const MAIL_STATUS: Record<string, [string, string]> = {
  PREPARED: ['не відправлено', '#b45309'],
  SENT: ['надіслано', '#15803d'],
  FAILED: ['помилка', '#dc2626'],
  DELIVERED: ['доставлено в систему', '#15803d'],
  VIEWED: ['переглянуто контрагентом', '#15803d'],
};

/** `edoMode` — режим «ЕДО»: організацій там немає, є учасники ЕДО (ч.1435). */
export default function EdoJournalPage({ edoMode = false }: { edoMode?: boolean } = {}) {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useOrgFilter();
  // ч.1058/ч.1110: надсилати з журналу може лише підтверджена організація
  // (підписана ключем КЕП заява про приєднання до оферти — кнопка «підтвердити» у довіднику
  // «Організації»). Бек дублює гейтом (403).
  const confirmedOrgIds = useMemo(
    () => new Set((orgList.data?.items ?? []).filter((o) => o.kep_confirmed_at).map((o) => o.id)),
    [orgList.data]);
  const isConfirmed = (id: string | null | undefined) => !!id && confirmedOrgIds.has(id);
  // ч.1315: окремі права ЕДО — надсилання і власний підпис, у кожній організації окремо.
  const sendOrgs = usePermissionsByOrg('edo.send.manage').availableOrgs;
  const signOrgs = usePermissionsByOrg('edo.sign.manage').availableOrgs;
  const sendIds = useMemo(() => new Set(sendOrgs.map((o) => o.id)), [sendOrgs]);
  const signIds = useMemo(() => new Set(signOrgs.map((o) => o.id)), [signOrgs]);
  const canSend = (id: string | null | undefined) => !!id && sendIds.has(id);
  const canSign = (id: string | null | undefined) => !!id && signIds.has(id);
  const inThis = edoMode ? 'у цього учасника' : 'в цій організації';
  const NO_SEND_TIP = `Недоступно: у вашій ролі немає права «ЕДО: надсилання документів контрагенту» ${inThis}`;
  const NO_SIGN_TIP = `Недоступно: у вашій ролі немає права «ЕДО: підписання документа власним КЕП» ${inThis}`;
  const NOT_CONFIRMED_TIP = edoMode
    ? 'Недоступно: учасник не підтверджений — підпишіть заяву про приєднання до оферти ключем КЕП учасника (розділ «Учасники ЕДО» → «підтвердити»)'
    : 'Недоступно: організація не підтверджена — підпишіть заяву про приєднання до оферти ключем КЕП цієї юрособи (довідник «Організації» → «підтвердити»)';
  const selectedOrgUnconfirmed = !!orgId && !!orgList.data && !isConfirmed(orgId);
  // Дефолт орг — ЛИШЕ при першому завантаженні; далі вибір «усі» ('')
  // не перетирається (2026-08-14).
  const orgDefaulted = useRef(false);
  useEffect(() => {
    if (orgDefaulted.current || !orgList.data) return;
    orgDefaulted.current = true;
    const first = pickDefaultOrg(orgList.data.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  // 0428: Вихідні / Вхідні (внутрішня доставка між користувачами системи).
  // ч.1431–1432: вкладка живе в адресі (`?direction=IN`): її не губить ні перемикання режиму, ні F5,
  // а старі адреси «ЕДО: Вхідні» відкривають потрібну вкладку.
  const direction: 'OUT' | 'IN' = searchParams.get('direction') === 'IN' ? 'IN' : 'OUT';
  const setDirection = (next: 'OUT' | 'IN') => setSearchParams(
    next === 'IN' ? { direction: 'IN' } : {}, { replace: true },
  );
  // Фільтри над таблицею (рішення власника 2026-08-14, як у Кабінеті ДПС) —
  // з 2026-08-22 виконуються СЕРВЕРОМ (параметри запиту, серверна пагінація).
  const [fltContractor, setFltContractor] = useState('');
  const [fltSignStatus, setFltSignStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const gridRefreshRef = useRef<(() => void) | null>(null);
  // ч.1433 (рішення власника): підпис пачкою — лише позначені галочками документи. Інша вибірка
  // (вкладка, фільтри) — інший перелік: позначки скидаємо, інакше підписали б те, чого вже не видно.
  // ag-Grid з getRowId тримає вибір між автооновленнями таблиці.
  const gridApiRef = useRef<GridApi<EdoRow> | null>(null);
  const [picked, setPicked] = useState<EdoRow[]>([]);
  useEffect(() => {
    gridApiRef.current?.deselectAll();
    setPicked((prev) => (prev.length ? [] : prev));
  }, [orgId, direction, fltContractor, fltSignStatus, dateFrom, dateTo]);
  const { datasource, total } = useInfiniteGridDatasource<EdoRow>(
    (limit, offset, sort) => api.get<{ items: EdoRow[]; total: number }>(
      '/edo/journal', {
        // Порожній orgId = «усі» (backend віддає всі доступні org'и).
        params: {
          ...(orgId ? { organization_id: orgId } : {}),
          direction,
          contractor_id: fltContractor || undefined,
          sign_status: fltSignStatus || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
          limit, offset,
        },
      }).then((r) => r.data),
    [orgId, direction, fltContractor, fltSignStatus, dateFrom, dateTo],
  );
  // Автооновлення журналу (як refetchInterval до серверної пагінації).
  useEffect(() => {
    const t = setInterval(() => gridRefreshRef.current?.(), 30_000);
    return () => clearInterval(t);
  }, []);

  const [mail, setMail] = useState<{ row: EdoRow; to: string; subject: string; body: string; save: boolean; chan: 'EMAIL' | 'SYSTEM' } | null>(null);
  const [signReq, setSignReq] = useState<{ row: EdoRow; to: string; chan: 'EMAIL' | 'SYSTEM' } | null>(null);
  const [ownSign, setOwnSign] = useState<{ row: EdoRow } | null>(null);
  const [batchSign, setBatchSign] = useState(false);
  // Підписання ВХІДНОГО (0428): отримувач авторизований — підписує прямо
  // в журналі (та сама модалка), submit іде на публічний механізм з
  // повною криптоперевіркою і звіркою коду (той самий sign_token).
  const [inSign, setInSign] = useState<{ row: EdoRow } | null>(null);
  // Спосіб власного підпису (2026-08-14): ключі сесії АБО віджет ІІТ
  // (токен/ХМАРНІ сервіси — SmartID тощо).
  // ч.1442: + Дія.Підпис (віджет id.gov.ua), якщо підключено.
  const [ownMethod, setOwnMethod] = useState<SignMethod>('session');
  // Ключі КЕП — СПІЛЬНИЙ реєстр сесії з Кабінетом ДПС (рішення власника
  // 2026-08-13): активований там ключ працює тут і навпаки.
  const [sessionKeys, setSessionKeys] = useState<StoredKey[]>(() => listKeys());
  const [selKeyId, setSelKeyId] = useState<string>('');
  const [signLink, setSignLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  // Попап «створити» (рішення власника 2026-08-13): відправлення
  // контрагенту без документа-підстави — файли на підпис/мейл.
  const [create, setCreate] = useState<{
    orgId: string; contractorId: string; to: string; subject: string; files: File[];
  } | null>(null);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const createContractor = (contractorsQ.data?.items ?? [])
    .find((c) => c.id === create?.contractorId);
  const createContactsQ = useQuery({
    queryKey: ['contractor-contacts', create?.contractorId],
    queryFn: () => api.get<{ email: string | null }[]>('/refs/contractor-contacts', {
      params: { contractor_id: create!.contractorId },
    }).then((r) => r.data),
    enabled: !!create?.contractorId,
  });
  const createEmails = useMemo(() => {
    const set = new Set<string>();
    const card = (createContractor as { email?: string | null } | undefined)?.email;
    if (card) set.add(card);
    for (const c of createContactsQ.data ?? []) if (c.email) set.add(c.email);
    return [...set];
  }, [createContractor, createContactsQ.data]);

  // Мутації оновлюють грід через refreshInfiniteCache (datasource поза
  // react-query кешем) + інвалідують запит панелі вкладень.
  const refresh = () => {
    gridRefreshRef.current?.();
    qc.invalidateQueries({ queryKey: ['edo-journal-doc'] });
  };

  // Збережені адреси контрагента (картка + контактні особи) — підказка
  // у полях email обох модалів (рішення власника 2026-08-13).
  const activeCounterparty = mail?.row.counterparty_id ?? signReq?.row.counterparty_id ?? null;
  const contactsQ = useQuery({
    queryKey: ['contractor-contacts', activeCounterparty],
    queryFn: () => api.get<{ email: string | null }[]>('/refs/contractor-contacts', {
      params: { contractor_id: activeCounterparty },
    }).then((r) => r.data),
    enabled: !!activeCounterparty,
  });
  const savedEmails = useMemo(() => {
    const set = new Set<string>();
    const cardEmail = mail?.row.counterparty_email ?? signReq?.row.counterparty_email;
    if (cardEmail) set.add(cardEmail);
    for (const c of contactsQ.data ?? []) if (c.email) set.add(c.email);
    return [...set];
  }, [mail?.row.counterparty_email, signReq?.row.counterparty_email, contactsQ.data]);
  const errText = (e: unknown) => {
    const d = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    return typeof d === 'string' ? d : null;
  };

  // 0428: підказка «контрагент є користувачем системи» (рішення власника
  // 2026-08-16: БЕЗ авто-маршрутизації — канал обирає юзер; підказка лише
  // якщо орг контрагента верифікована хоч одним криптопідтвердженим КЕП).
  const checkCode = mail?.row.counterparty_code
    ?? signReq?.row.counterparty_code ?? null;
  const sysQ = useQuery({
    queryKey: ['edo-recipient-check', checkCode],
    queryFn: () => api.get<{ in_system: boolean; orgs: string[] }>(
      '/edo/recipient-check', { params: { code: checkCode } },
    ).then((r) => r.data),
    enabled: !!checkCode && checkCode.length >= 8,
    staleTime: 600_000,
  });
  const inSystem = !!sysQ.data?.in_system;

  const sendInternal = async (row: EdoRow, mode: 'DOC' | 'SIGN') => {
    setBusy(true);
    try {
      const r = await api.post<{ delivered: number; orgs: string[] }>(
        `/edo/journal/${row.id}/send-internal`, { mode },
        { headers: { 'X-Silent-422': '1' } });
      refresh();
      setMail(null);
      setSignReq(null);
      window.alert(`Доставлено в систему: ${r.data.orgs.join(', ')}`);
    } catch (e) {
      window.alert(errText(e) ?? 'Не вдалося доставити в систему');
    } finally {
      setBusy(false);
    }
  };

  // Попап-перегляд відправленого/отриманого документа (рішення власника
  // 2026-08-19): клік по назві у колонці «Документ» — PDF/зображення у
  // вікні, інше — кнопкою «завантажити».
  const [docView, setDocView] = useState<{
    row: EdoRow; url: string; mime: string } | null>(null);
  const viewFrameRef = useRef<HTMLIFrameElement>(null);
  const openDocView = async (r: EdoRow) => {
    if (!r.attachment_id) return;
    try {
      if (r.direction === 'IN') {
        api.post(`/edo/journal/${r.id}/viewed`).catch(() => {});
      }
      const blob = await attachments.downloadBlob(r.attachment_id);
      setDocView({ row: r, url: URL.createObjectURL(blob),
        mime: blob.type || 'application/octet-stream' });
    } catch {
      window.alert('Не вдалося відкрити документ');
    }
  };
  const closeDocView = () => {
    if (docView) URL.revokeObjectURL(docView.url);
    setDocView(null);
  };

  // Вхідний запис: скачати документ (+ позначка «переглянуто» відправнику).
  const downloadIncoming = async (r: EdoRow) => {
    try {
      api.post(`/edo/journal/${r.id}/viewed`).catch(() => {});
      const blob = await attachments.downloadBlob(r.attachment_id!);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = r.subject ?? 'документ';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      refresh();
    } catch {
      window.alert('Не вдалося скачати файл');
    }
  };

  const sendMail = async () => {
    if (!mail) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', mail.row.organization_id);
      fd.append('document_type', mail.row.document_type);
      fd.append('document_id', mail.row.document_id);
      fd.append('journal_id', mail.row.id);
      fd.append('to_email', mail.to.trim());
      fd.append('subject', mail.subject);
      fd.append('body_text', mail.body);
      await api.post('/edo/send-email', fd, { headers: { 'X-Silent-422': '1' } });
      // «Зберегти адресу» → контактна особа картки контрагента.
      const addr = mail.to.trim();
      if (mail.save && mail.row.counterparty_id
          && !savedEmails.some((e) => e.toLowerCase() === addr.toLowerCase())) {
        try {
          await api.post('/refs/contractor-contacts', {
            contractor_id: mail.row.counterparty_id,
            name: addr, email: addr, is_main: false,
          });
          qc.invalidateQueries({ queryKey: ['contractor-contacts'] });
        } catch { /* збереження адреси не блокує відправку */ }
      }
      refresh();
      setMail(null);
      window.alert('Лист надіслано');
    } catch (e) {
      window.alert(errText(e) ?? 'Не вдалося надіслати лист');
    } finally {
      setBusy(false);
    }
  };

  // Власний підпис КЕП (кнопка «підписати ЕЦП»): ключ зі СПІЛЬНОГО
  // реєстру сесії (KeyAuthPanel, як у Кабінеті ДПС) → CAdES у браузері.
  const doOwnSign = async () => {
    if (!ownSign) return;
    setBusy(true);
    try {
      await loadEuSign();
      const keys = listKeys();
      if (!keys.length) {
        window.alert('Спершу зчитайте ключ КЕП (кнопка вище)');
        return;
      }
      // ЄДИНА логіка вибору ключа з Кабінетом ДПС (уніфікація власника
      // 2026-08-17): без ручного вибору — підбір за КОДОМ юрособи запису
      // (pickKeyForCode активує з лінивим відновленням після F5).
      let chosenId = selKeyId;
      if (!chosenId) {
        const org = orgList.data?.items.find(
          (o) => o.id === ownSign.row.organization_id);
        const owner = await pickKeyForCode(
          (org?.edrpou || org?.inn || ''), org?.name ?? 'цієї юрособи');
        chosenId = keys.find((k) => k.owner.serial === owner.serial)?.id
          ?? activeKeyId() ?? keys[0]!.id;
      }
      // Пара «підпис + печатка» однієї орг (2026-08-14): якщо у сесії є
      // ДВА ключі з одним кодом (особистий підпис і печатка організації)
      // — накладаємо ОБИДВА (печатка окремим p7s, як у «Вчасно»).
      const chosen = keys.find((k) => k.id === chosenId) ?? keys[0]!;
      const sameOrg = keys.filter((k) => keyCode(k.owner) === keyCode(chosen.owner));
      const signKey = sameOrg.find((k) => !isSealOwner(k.owner)) ?? chosen;
      const sealKey = sameOrg.find((k) => isSealOwner(k.owner)
        && k.owner.serial !== signKey.owner.serial) ?? null;

      const owner = await activateKey(signKey.id);
      const blob = await attachments.downloadBlob(ownSign.row.attachment_id!);
      const data = new Uint8Array(await blob.arrayBuffer());
      // CAdES-T з міткою часу TSP (2026-08-14); TSP лежить → BES-фолбек.
      const { signature, timestamped } = await signDataInternalCadesT(data);
      let sealB64: string | null = null;
      let sealName = '';
      let sealTimestamped = true;
      if (sealKey) {
        await activateKey(sealKey.id);
        const sealRes = await signDataInternalCadesT(data);
        sealB64 = bytesToBase64(sealRes.signature);
        sealTimestamped = sealRes.timestamped;
        sealName = sealKey.owner.subjOrg || sealKey.owner.subjCN || 'печатка';
        await activateKey(signKey.id);  // повернути активним ключ підпису
      }
      await api.post(`/edo/journal/${ownSign.row.id}/own-sign`, {
        signature_b64: bytesToBase64(signature),
        signer_name: owner?.subjCN ?? '',
        signer_edrpou: keyCode(owner),
        timestamped,
        seal_signature_b64: sealB64,
        seal_name: sealName,
        seal_timestamped: sealTimestamped,
      }, { headers: { 'X-Silent-422': '1' } });
      refresh();
      setOwnSign(null);
    } catch (e) {
      window.alert(errText(e)
        ?? (e as { message?: string })?.message ?? 'Не вдалося підписати');
    } finally {
      setBusy(false);
    }
  };

  const doInSign = async () => {
    if (!inSign?.row.sign_token) return;
    setBusy(true);
    try {
      await loadEuSign();
      const keys = listKeys();
      if (!keys.length) {
        window.alert('Спершу зчитайте ключ КЕП (блок «Ключі КЕП» вище)');
        return;
      }
      let chosenId = selKeyId;
      if (!chosenId) {
        const org = orgList.data?.items.find(
          (o) => o.id === inSign.row.organization_id);
        const owner = await pickKeyForCode(
          (org?.edrpou || org?.inn || ''), org?.name ?? 'вашої юрособи');
        chosenId = keys.find((k) => k.owner.serial === owner.serial)?.id
          ?? activeKeyId() ?? keys[0]!.id;
      }
      const chosen = keys.find((k) => k.id === chosenId) ?? keys[0]!;
      await activateKey(chosen.id);
      const blob = await attachments.downloadBlob(inSign.row.attachment_id!);
      const data = new Uint8Array(await blob.arrayBuffer());
      const { signature } = await signDataInternalCadesT(data);
      await api.post(`/edo-public/sign/${inSign.row.sign_token}/submit`, {
        signature_b64: bytesToBase64(signature),
        signer_name: chosen.owner.subjCN ?? '',
        signer_edrpou: keyCode(chosen.owner),
      }, { headers: { 'X-Silent-422': '1' } });
      refresh();
      setInSign(null);
      window.alert('Документ підписано');
    } catch (e) {
      window.alert(errText(e)
        ?? (e as { message?: string })?.message ?? 'Не вдалося підписати');
    } finally {
      setBusy(false);
    }
  };

  const sendSignRequest = async () => {
    if (!signReq) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', signReq.row.organization_id);
      fd.append('journal_id', signReq.row.id);
      fd.append('to_email', signReq.to.trim());
      const r = await api.post<{ link: string }>(
        '/edo/sign-request', fd, { headers: { 'X-Silent-422': '1' } });
      refresh();
      setSignReq(null);
      setSignLink(r.data.link);
    } catch (e) {
      window.alert(errText(e) ?? 'Не вдалося створити запит підпису');
    } finally {
      setBusy(false);
    }
  };

  // «посилання»: перший клік теж переводить у «очікуємо підпису»
  // (рішення власника) — тому через sign-request без email.
  // ВАЖЛИВО: clipboard НЕ пишемо тут — Safari забороняє writeText після
  // await (жест «протух») → показуємо модал, копіювання кнопкою в ньому.
  const copyLink = async (r: EdoRow) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', r.organization_id);
      fd.append('journal_id', r.id);
      const resp = await api.post<{ link: string }>(
        '/edo/sign-request', fd, { headers: { 'X-Silent-422': '1' } });
      refresh();
      setSignLink(resp.data.link);
    } catch (e) {
      window.alert(errText(e) ?? 'Не вдалося створити посилання');
    } finally {
      setBusy(false);
    }
  };

  // Скачування підписаного (рішення власника 2026-08-14): 3 формати.
  const [dl, setDl] = useState<EdoRow | null>(null);
  const downloadFormat = async (r: EdoRow, format: 'pdf' | 'p7s' | 'zip') => {
    try {
      const resp = await api.get(`/edo/journal/${r.id}/download`, {
        params: { format }, responseType: 'blob' });
      const base = (r.subject ?? 'документ').replace(/\.[^.]+$/, '');
      const name = format === 'zip' ? `${base} (підписано).zip`
        : format === 'p7s' ? `${base} (підпис контрагента).p7s`
        : `${base}.pdf`;
      const url = URL.createObjectURL(resp.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      setDl(null);
    } catch {
      window.alert('Не вдалося скачати файл');
    }
  };

  const submitCreate = async () => {
    if (!create || !create.orgId || !create.contractorId
        || create.files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', create.orgId);
      fd.append('contractor_id', create.contractorId);
      fd.append('to_email', create.to.trim());
      fd.append('subject', create.subject.trim());
      for (const f of create.files) fd.append('files', f);
      await api.post('/edo/create', fd, { headers: { 'X-Silent-422': '1' } });
      refresh();
      setCreate(null);
    } catch (e) {
      window.alert(errText(e) ?? 'Не вдалося створити відправлення');
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (r: EdoRow) => {
    if (!window.confirm(`Прибрати з черги «${r.subject ?? 'документ'}»?`)) return;
    try {
      await api.delete(`/edo/journal/${r.id}`);
      refresh();
    } catch (e) {
      window.alert(errText(e) ?? 'Не вдалося видалити запис');
    }
  };

  const btn = { fontSize: '0.75rem', padding: '0.1rem 0.4rem' } as const;

  // colId сортованих колонок = ключі whitelist серверного сортування
  // (created_at/subject/mail_status/sign_status); поліморфні (Відправник/
  // Кому) та колонки дій сервер не сортує — sortable:false.
  const cols = useMemo<ColDef<EdoRow>[]>(() => [
    ...(direction === 'IN' ? [{
      colId: '_sel', headerName: '', width: 40,
      headerTooltip: 'Позначте документи, щоб підписати їх одним ключем',
      sortable: false, resizable: false, suppressMovable: true,
      checkboxSelection: true,
    } satisfies ColDef<EdoRow>] : []),
    {
      colId: 'created_at', field: 'created_at', headerName: 'Дата', width: 130,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit' }) : '',
    },
    {
      colId: 'sender', headerName: 'Відправник', width: 150, sortable: false,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => !r ? '' : r.direction === 'IN'
        ? (
          <span title="Надіслано вам через Облік+">
            {r.peer_org_name ?? '—'}{' '}
            <span style={{ color: '#2563eb', border: '1px solid var(--badge-blue-bd)',
              borderRadius: 4, fontSize: '0.68rem', fontWeight: 700,
              padding: '0 4px' }}>в системі</span>
          </span>
        )
        : (r.sender_name ?? '—'),
    },
    {
      colId: 'subject', field: 'subject', headerName: 'Документ',
      flex: 1, minWidth: 200,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => !r ? '' : r.attachment_id ? (
        <a style={{ cursor: 'pointer' }}
          title="Переглянути документ"
          onClick={(e) => { e.stopPropagation(); void openDocView(r); }}>
          {r.subject ?? DOC_TABLE_NAMES_UK[r.document_type] ?? r.document_type}
        </a>
      ) : (
        <span title={DOC_TABLE_NAMES_UK[r.document_type]
          ?? (r.document_type === 'edo.message' ? 'відправлення ЕДО'
            : r.document_type === 'edo.service_agreement' ? 'заява про приєднання до оферти Облік+' : r.document_type)}>
          {r.subject ?? DOC_TABLE_NAMES_UK[r.document_type] ?? r.document_type}
        </span>
      ),
    },
    {
      colId: 'counterparty', headerName: 'Кому', width: 180, sortable: false,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => !r ? '' : r.direction === 'IN'
        ? (r.sender_name ?? '—')
        : r.counterparty_name
          ? <span title={r.recipient_email ?? r.counterparty_email ?? undefined}>{r.counterparty_name}</span>
          : (r.recipient_email ?? '—'),
    },
    {
      colId: 'mail_action', headerName: '', width: 130, sortable: false,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => !r ? '' : r.direction === 'IN' ? (
        <button style={btn} disabled={busy || !r.attachment_id}
          title="Скачати документ (відправник побачить «переглянуто»)"
          onClick={() => void downloadIncoming(r)}>
          скачати
        </button>
      ) : (
        <button style={btn} disabled={busy || !isConfirmed(r.organization_id) || !canSend(r.organization_id)}
          title={!canSend(r.organization_id) ? NO_SEND_TIP
            : isConfirmed(r.organization_id) ? 'Надіслати документ на email контрагента' : NOT_CONFIRMED_TIP}
          onClick={() => setMail({
            row: r,
            to: r.counterparty_email ?? r.recipient_email ?? '',
            subject: (r.subject ?? 'Документ').replace(/\.[^.]+$/, ''),
            body: `Доброго дня!\n\nНадсилаємо документ «${(r.subject ?? 'документ').replace(/\.[^.]+$/, '')}».\n`,
            save: false, chan: 'EMAIL',
          })}>
          відправити мейл
        </button>
      ),
    },
    {
      colId: 'mail_status', headerName: 'Статус мейла', width: 125,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => {
        if (!r) return '';
        if (r.direction === 'IN') {
          return r.viewed_at
            ? <span style={{ color: 'var(--muted)' }}>переглянуто</span>
            : <span style={{ color: '#2563eb', fontWeight: 700 }}>нове</span>;
        }
        const [label, color] = MAIL_STATUS[r.status] ?? [r.status, '#334155'];
        return (
          <span style={{ color, fontWeight: 600 }}
            title={r.status === 'FAILED' ? (r.error_message ?? undefined) : undefined}>
            {label}
          </span>
        );
      },
    },
    {
      colId: 'ecp_actions', headerName: 'ЕЦП', width: 215, sortable: false,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => !r ? '' : r.direction === 'IN' ? (
        // Вхідний: якщо просять підпис — підписати за тим самим
        // публічним посиланням (повна криптоперевірка + звірка коду).
        r.sign_status === 'WAITING' && r.sign_token ? (
          <button className="primary" style={btn} disabled={busy}
            title="Підписати документ вашим КЕП"
            onClick={() => setInSign({ row: r })}>
            підписати КЕП
          </button>
        ) : r.sign_status === 'SIGNED' ? (
          <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600, fontSize: '0.78rem' }}>
            ✓ підписано
          </span>
        ) : <span style={{ color: 'var(--muted)' }}>—</span>
      ) : (
        <span style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {!r.own_signed ? (
            <button style={btn} disabled={busy || !canSign(r.organization_id)}
              title={canSign(r.organization_id) ? 'Підписати документ власним КЕП (файловий ключ)' : NO_SIGN_TIP}
              onClick={() => setOwnSign({ row: r })}>
              підписати ЕЦП
            </button>
          ) : (
            <>
              <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600, fontSize: '0.78rem' }}
                title="Підписано вашим КЕП">✓ підписано</span>
              {r.sign_status !== 'SIGNED' && (
                <>
                  {/* Після ВІДПРАВКИ email кнопка деактивується (0427);
                      «посилання» лишається активною до підписання. */}
                  <button style={btn}
                    disabled={busy || !r.counterparty_code || r.sign_email_sent || !isConfirmed(r.organization_id)
                      || !canSend(r.organization_id)}
                    title={!canSend(r.organization_id) ? NO_SEND_TIP
                      : !isConfirmed(r.organization_id) ? NOT_CONFIRMED_TIP : r.sign_email_sent
                      ? 'Посилання вже надіслано контрагенту на email'
                      : r.counterparty_code
                        ? 'Надіслати контрагенту посилання для підписання на email'
                        : 'Недоступно: на картці контрагента не заповнено ЄДРПОУ/РНОКПП — підпис звіряється з цим кодом'}
                    onClick={() => setSignReq({
                      row: r, to: r.counterparty_email ?? r.recipient_email ?? '',
                      chan: 'EMAIL' })}>
                    відправити на підпис
                  </button>
                  <button style={btn}
                    disabled={busy || !r.counterparty_code || !isConfirmed(r.organization_id) || !canSend(r.organization_id)}
                    title={!canSend(r.organization_id) ? NO_SEND_TIP
                      : !isConfirmed(r.organization_id) ? NOT_CONFIRMED_TIP : r.counterparty_code
                      ? 'Скопіювати посилання для підписання'
                      : 'Недоступно: на картці контрагента не заповнено ЄДРПОУ/РНОКПП'}
                    onClick={() => void copyLink(r)}>
                    посилання
                  </button>
                </>
              )}
            </>
          )}
        </span>
      ),
    },
    {
      colId: 'sign_status', field: 'sign_status',
      headerName: 'Статус підпису', width: 185,
      cellRenderer: ({ data: r }: { data?: EdoRow }) => !r ? '' : r.sign_status === 'SIGNED' ? (
        <span style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}
            title={r.signer_info ?? undefined}>
            {r.direction === 'IN' ? 'підписано' : 'підписано контрагентом'}
          </span>
          <button style={btn} title="Скачати підписаний документ (PDF / p7s / ZIP з квитанцією)"
            onClick={() => setDl(r)}>
            скачати
          </button>
        </span>
      ) : r.sign_status === 'WAITING' ? (
        <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}>
          {r.direction === 'IN' ? 'очікує вашого підпису' : 'очікуємо підпису'}
        </span>
      ) : <span style={{ color: 'var(--muted)' }}>—</span>,
    },
    {
      colId: 'remove', headerName: '', width: 40, sortable: false,
      // ч.1423: рядок із запитом чи підписом контрагента — історія обміну, його не прибирають.
      cellRenderer: ({ data: r }: { data?: EdoRow }) => r?.status === 'PREPARED' && !r.sign_status
        && r.direction !== 'IN' && canSend(r.organization_id) ? (
        <button className="danger" style={btn} title="Прибрати з черги"
          onClick={() => void removeEntry(r)}>×</button>
      ) : null,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [orgId, busy, direction, sendIds, signIds]);

  return (
    <div className="card">
      {/* Верстка як у Кабінеті ДПС (рішення власника 2026-08-14):
          тулбар (назва + створити) → Ключі КЕП → рядок фільтрів. */}
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          Журнал ЕДО<TitleTotal total={total} />
        </h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          disabled={!isConfirmed(orgId || pickDefaultOrg(orgList.data?.items)?.id)
            || !canSend(orgId || pickDefaultOrg(orgList.data?.items)?.id)}
          title={!canSend(orgId || pickDefaultOrg(orgList.data?.items)?.id) ? NO_SEND_TIP
            : isConfirmed(orgId || pickDefaultOrg(orgList.data?.items)?.id)
            ? 'Надіслати контрагенту файли на підпис або мейл'
            : NOT_CONFIRMED_TIP}
          onClick={() => setCreate({
            orgId: orgId || pickDefaultOrg(orgList.data?.items)?.id || '',
            contractorId: '', to: '', subject: '', files: [] })}>
          + створити
        </button>
      </div>
      {selectedOrgUnconfirmed && (
        <div style={{ margin: '0.5rem 0', padding: '0.5rem 0.75rem', borderRadius: 6,
                      background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
                      border: '1px solid var(--badge-amber-bd)', fontSize: '0.85rem' }}>
          {edoMode ? (<>
            Цей учасник ще <b>не підтверджений</b>: надсилати документи контрагентам
            не можна. Підтвердіть його в розділі{' '}
            <Link to="/organizations">Учасники ЕДО</Link> кнопкою «підтвердити» — це
            підписання заяви про приєднання до оферти ключем КЕП учасника (ЄДРПОУ/РНОКПП
            ключа має збігатися з кодом учасника); після цього кнопки відправки стануть
            активними. Підписувати свої документи можна вже зараз.
          </>) : (<>
            Ця організація ще <b>не підтверджена</b>: надсилати документи контрагентам
            не можна. Підтвердіть її у довіднику{' '}
            <Link to="/organizations">Організації</Link> кнопкою «підтвердити» — це
            підписання заяви про приєднання до оферти ключем КЕП юрособи (ЄДРПОУ/РНОКПП
            ключа має збігатися з кодом організації); після цього кнопки відправки стануть
            активними. Підписувати свої документи можна вже зараз.
          </>)}
        </div>
      )}

      {/* ч.1442: людина прийшла зі сторінки підпису за посиланням і зареєструвалась — пропонуємо
          зберегти той документ у журнал (поки посилання живе). */}
      <PendingClaim onSaved={() => gridRefreshRef.current?.()} />

      {/* Ключі КЕП — ЄДИНИЙ блок як у Кабінеті ДПС: той самий реєстр
          сесії, активований там ключ працює тут і навпаки. */}
      <KeyAuthPanel onKeysChange={setSessionKeys} />

      {/* 0428: Вихідні / Вхідні — той самий стиль вкладок, що у Кабінеті ДПС. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
        <div className="seg-tabs">
          <button className={direction === 'OUT' ? 'active' : ''}
            onClick={() => setDirection('OUT')}>Вихідні</button>
          <button className={direction === 'IN' ? 'active' : ''}
            onClick={() => setDirection('IN')}>Вхідні</button>
        </div>
        {direction === 'IN' && (
          <button className="primary" disabled={picked.length < 2} onClick={() => setBatchSign(true)}
            title={picked.length < 2 ? 'Позначте галочками щонайменше два документи'
              : 'Підписати позначені документи одним ключем'}>
            підписати пачкою{picked.length >= 2 ? ` (${picked.length})` : ''}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
        marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">усі</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ width: 220, flex: '0 0 auto' }}>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={fltContractor}
            onChange={setFltContractor}
            placeholder="Контрагент — усі"
          />
        </div>
        {fltContractor && (
          <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
            title="Скинути фільтр контрагента"
            onClick={() => setFltContractor('')}>×</button>
        )}
        <select value={fltSignStatus} onChange={(e) => setFltSignStatus(e.target.value)}
          style={{ width: 185, flex: '0 0 auto' }}
          title="Фільтр за статусом підпису">
          <option value="">Статус підпису — усі</option>
          <option value="NONE">не запитувався</option>
          <option value="WAITING">очікуємо підпису</option>
          <option value="SIGNED">підписано контрагентом</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>з</span>
          <div style={{ width: 140 }}>
            <DatePopover value={dateFrom} onChange={setDateFrom} />
          </div>
          <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>по</span>
          <div style={{ width: 140 }}>
            <DatePopover value={dateTo} onChange={setDateTo} />
          </div>
          {(dateFrom || dateTo) && (
            <button style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
              onClick={() => { setDateFrom(''); setDateTo(''); }}>×</button>
          )}
        </div>
      </div>

      {batchSign && (
        <EdoBatchSignDialog rows={picked} onChanged={refresh}
          onClose={() => { setBatchSign(false); gridApiRef.current?.deselectAll(); setPicked([]); }} />
      )}

      <InfiniteDocGrid<EdoRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="edo-journal"
        refreshRef={gridRefreshRef}
        getRowId={(r) => r.id}
        apiRef={gridApiRef}
        rowSelection="multiple"
        onSelectionChanged={setPicked}
        isRowSelectable={(r) => r.direction === 'IN' && r.sign_status === 'WAITING'
          && !!r.sign_token && !!r.attachment_id}
        headerLeft={direction === 'IN' ? (
          // ч.1434: таблиця вантажиться порціями, вибрати можна лише те, що вже завантажено.
          <span style={{ fontSize: '0.8rem', display: 'inline-flex', gap: '0.7rem' }}>
            <a style={{ cursor: 'pointer' }} title="Позначити всі завантажені документи, що чекають на підпис"
              onClick={() => gridApiRef.current?.forEachNode((n) => { if (n.selectable) n.setSelected(true); })}>
              позначити усі
            </a>
            {picked.length > 0 && (
              <a style={{ cursor: 'pointer' }} onClick={() => { gridApiRef.current?.deselectAll(); setPicked([]); }}>
                зняти позначки
              </a>
            )}
          </span>
        ) : undefined}
      />

      {create && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setCreate(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(560px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Створити відправлення</h3>
            <div className="field">
              <label>Відправник <span style={{ color: '#dc2626' }}>*</span></label>
              <select value={create.orgId}
                onChange={(e) => setCreate({ ...create, orgId: e.target.value })}>
                <option value="">— оберіть —</option>
                {orgList.data?.items.map((o) => (
                  <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Контрагент <span style={{ color: '#dc2626' }}>*</span></label>
              <SearchSelect
                items={(contractorsQ.data?.items ?? []).map((c) => ({
                  id: c.id, label: c.name, hint: c.edrpou ?? null,
                }))}
                value={create.contractorId}
                onChange={(id) => setCreate({ ...create, contractorId: id, to: '' })}
              />
            </div>
            <div className="field">
              <label>Email контрагента</label>
              <input value={create.to} list="edo-create-emails"
                disabled={!create.contractorId}
                placeholder={!create.contractorId
                  ? 'спершу оберіть контрагента'
                  : createEmails.length
                    ? 'оберіть збережену адресу або введіть нову'
                    : 'введіть email'}
                onChange={(e) => setCreate({ ...create, to: e.target.value })} />
              <datalist id="edo-create-emails">
                {createEmails.map((e) => <option key={e} value={e} />)}
              </datalist>
            </div>
            <div className="field">
              <label>Тема</label>
              <input value={create.subject}
                placeholder="назва відправлення (за замовч. — імʼя файла)"
                onChange={(e) => setCreate({ ...create, subject: e.target.value })} />
            </div>
            <div className="field">
              <label>Файли <span style={{ color: '#dc2626' }}>*</span> (PDF/JPEG/PNG/HEIC, до 10 МБ)</label>
              <input type="file" multiple
                accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
                onChange={(e) => setCreate({
                  ...create, files: [...(e.target.files ?? [])] })} />
              {create.files.length > 0 && (
                <div style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: 4 }}>
                  {create.files.map((f) => f.name).join(' · ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setCreate(null)}>скасувати</button>
              <button className="primary"
                disabled={busy || !create.orgId || !create.contractorId
                  || create.files.length === 0}
                title={!create.orgId ? (edoMode ? 'Оберіть учасника' : 'Оберіть організацію')
                  : !create.contractorId ? 'Оберіть контрагента'
                  : create.files.length === 0 ? 'Додайте файли' : undefined}
                onClick={() => void submitCreate()}>
                {busy ? 'збереження…' : 'зберегти'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mail && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setMail(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(520px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Надіслати на email</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
              Документ: <b>{mail.row.subject ?? '—'}</b>
              {mail.row.counterparty_name && <> · {mail.row.counterparty_name}</>}
            </p>
            {inSystem && (
              <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
                borderRadius: 6, padding: '0.45rem 0.6rem', margin: '0 0 0.6rem',
                fontSize: '0.82rem' }}>
                <div style={{ marginBottom: '0.3rem' }}>
                  Контрагент <b>{sysQ.data!.orgs.join(', ')}</b> — користувач
                  Облік+. Куди надіслати?
                </div>
                <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" checked={mail.chan === 'SYSTEM'}
                    onChange={() => setMail({ ...mail, chan: 'SYSTEM' })} />
                  в систему — документ зʼявиться у «Вхідних» журналу ЕДО контрагента
                </label>
                <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" checked={mail.chan === 'EMAIL'}
                    onChange={() => setMail({ ...mail, chan: 'EMAIL' })} />
                  на email
                </label>
              </div>
            )}
            {mail.chan !== 'SYSTEM' && (<>
            <div className="field">
              <label>Кому (email)</label>
              <input value={mail.to} autoFocus list="edo-saved-emails"
                placeholder={savedEmails.length
                  ? 'оберіть збережену адресу або введіть нову'
                  : 'введіть email'}
                onChange={(e) => setMail({ ...mail, to: e.target.value })} />
              <datalist id="edo-saved-emails">
                {savedEmails.map((e) => <option key={e} value={e} />)}
              </datalist>
            </div>
            {mail.row.counterparty_id && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem',
                fontSize: '0.82rem', margin: '0 0 0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={mail.save}
                  onChange={(e) => setMail({ ...mail, save: e.target.checked })} />
                зберегти адресу на картці контрагента
              </label>
            )}
            <div className="field">
              <label>Тема</label>
              <input value={mail.subject}
                onChange={(e) => setMail({ ...mail, subject: e.target.value })} />
            </div>
            <div className="field">
              <label>Текст листа</label>
              <textarea value={mail.body} rows={3}
                onChange={(e) => setMail({ ...mail, body: e.target.value })}
                style={{ width: '100%', resize: 'vertical' }} />
            </div>
            </>)}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setMail(null)}>скасувати</button>
              {mail.chan === 'SYSTEM' ? (
                <button className="primary" disabled={busy}
                  onClick={() => void sendInternal(mail.row, 'DOC')}>
                  {busy ? 'доставка…' : 'надіслати в систему'}
                </button>
              ) : (
                <button className="primary" disabled={busy || !mail.to.trim()}
                  onClick={() => void sendMail()}>
                  {busy ? 'надсилання…' : 'надіслати'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {ownSign && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setOwnSign(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(760px, 94vw)',
            maxHeight: '92vh', overflowY: 'auto',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Підписати ЕЦП (власний КЕП)</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0 0 0.6rem' }}>
              Документ: <b>{ownSign.row.subject ?? '—'}</b>.
            </p>
            {/* Спосіб підпису: ключі сесії або віджет (токен/хмарні). */}
            <SignMethodTabs value={ownMethod} onChange={setOwnMethod} />
            {ownMethod !== 'session' ? (
              <MethodWidget method={ownMethod}
                getData={async () => {
                  const blob = await attachments.downloadBlob(ownSign.row.attachment_id!);
                  return new Uint8Array(await blob.arrayBuffer());
                }}
                onSigned={async (sigB64) => {
                  try {
                    await api.post(`/edo/journal/${ownSign.row.id}/own-sign`,
                      { signature_b64: sigB64 },
                      { headers: { 'X-Silent-422': '1' } });
                    refresh();
                    setOwnSign(null);
                  } catch (e) {
                    throw new Error(errText(e) ?? 'Не вдалося зберегти підпис');
                  }
                }}
              />
            ) : sessionKeys.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
                Немає зчитаних ключів КЕП.
              </p>
            ) : null}
            {ownMethod === 'session' && sessionKeys.length > 1 && (
              <div className="field" style={{ marginTop: '0.4rem' }}>
                <label>Підписати ключем</label>
                <select value={selKeyId || activeKeyId() || sessionKeys[0]!.id}
                  onChange={(e) => setSelKeyId(e.target.value)}>
                  {sessionKeys.map((k) => (
                    <option key={k.id} value={k.id}>
                      {(k.owner.subjOrg || k.owner.subjCN || 'ключ')}
                      {keyCode(k.owner) ? ` — ${keyCode(k.owner)}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.7rem' }}>
              <button onClick={() => setOwnSign(null)}>скасувати</button>
              {ownMethod === 'session' && (
                <button className="primary" disabled={busy || sessionKeys.length === 0}
                  title={sessionKeys.length === 0
                    ? 'Спершу зчитайте ключ КЕП' : undefined}
                  onClick={() => void doOwnSign()}>
                  {busy ? 'підписання…' : 'підписати'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {inSign && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setInSign(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(760px, 94vw)',
            maxHeight: '92vh', overflowY: 'auto',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Підписати вхідний документ</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0 0 0.6rem' }}>
              Документ: <b>{inSign.row.subject ?? '—'}</b> від{' '}
              <b>{inSign.row.peer_org_name ?? 'відправника'}</b>
            </p>
            <SignMethodTabs value={ownMethod} onChange={setOwnMethod} />
            {ownMethod !== 'session' ? (
              <MethodWidget method={ownMethod}
                getData={async () => {
                  const blob = await attachments.downloadBlob(inSign.row.attachment_id!);
                  return new Uint8Array(await blob.arrayBuffer());
                }}
                onSigned={async (sigB64) => {
                  try {
                    await api.post(`/edo-public/sign/${inSign.row.sign_token}/submit`,
                      { signature_b64: sigB64 },
                      { headers: { 'X-Silent-422': '1' } });
                    refresh();
                    setInSign(null);
                    window.alert('Документ підписано');
                  } catch (e) {
                    throw new Error(errText(e) ?? 'Не вдалося зберегти підпис');
                  }
                }}
              />
            ) : sessionKeys.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
                Немає зчитаних ключів КЕП.
              </p>
            ) : null}
            {ownMethod === 'session' && sessionKeys.length > 1 && (
              <div className="field" style={{ marginTop: '0.4rem' }}>
                <label>Підписати ключем</label>
                <select value={selKeyId || activeKeyId() || sessionKeys[0]!.id}
                  onChange={(e) => setSelKeyId(e.target.value)}>
                  {sessionKeys.map((k) => (
                    <option key={k.id} value={k.id}>
                      {(k.owner.subjOrg || k.owner.subjCN || 'ключ')}
                      {keyCode(k.owner) ? ` — ${keyCode(k.owner)}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.7rem' }}>
              <button onClick={() => setInSign(null)}>скасувати</button>
              {ownMethod === 'session' && (
                <button className="primary" disabled={busy || sessionKeys.length === 0}
                  title={sessionKeys.length === 0
                    ? 'Спершу зчитайте ключ КЕП' : undefined}
                  onClick={() => void doInSign()}>
                  {busy ? 'підписання…' : 'підписати'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {signReq && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setSignReq(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(520px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>На підпис контрагенту (КЕП/ЕЦП)</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
              Документ: <b>{signReq.row.subject ?? '—'}</b>
            </p>
            {inSystem && (
              <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
                borderRadius: 6, padding: '0.45rem 0.6rem', margin: '0 0 0.6rem',
                fontSize: '0.82rem' }}>
                <div style={{ marginBottom: '0.3rem' }}>
                  Контрагент <b>{sysQ.data!.orgs.join(', ')}</b> — користувач
                  Облік+. Як надіслати запит підпису?
                </div>
                <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" checked={signReq.chan === 'SYSTEM'}
                    onChange={() => setSignReq({ ...signReq, chan: 'SYSTEM' })} />
                  в систему — документ зʼявиться у «Вхідних» контрагента з кнопкою «підписати КЕП»
                </label>
                <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="radio" checked={signReq.chan === 'EMAIL'}
                    onChange={() => setSignReq({ ...signReq, chan: 'EMAIL' })} />
                  посиланням на email
                </label>
              </div>
            )}
            {signReq.chan !== 'SYSTEM' && (
            <div className="field">
              <label>Email контрагента</label>
              <input value={signReq.to} autoFocus list="edo-saved-emails-sign"
                placeholder="email для відправки посилання"
                onChange={(e) => setSignReq({ ...signReq, to: e.target.value })} />
              <datalist id="edo-saved-emails-sign">
                {savedEmails.map((e) => <option key={e} value={e} />)}
              </datalist>
            </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setSignReq(null)}>скасувати</button>
              {signReq.chan === 'SYSTEM' ? (
                <button className="primary" disabled={busy}
                  onClick={() => void sendInternal(signReq.row, 'SIGN')}>
                  {busy ? 'доставка…' : 'надіслати в систему'}
                </button>
              ) : (
                <button className="primary" disabled={busy || !signReq.to.trim()}
                  onClick={() => void sendSignRequest()}>
                  {busy ? 'відправка…' : 'відправити на підпис'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {dl && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDl(null)}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(480px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Скачати підписаний документ</h3>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0 0 0.7rem' }}>
              <b>{dl.subject ?? 'документ'}</b> — оберіть формат:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              <button onClick={() => void downloadFormat(dl, 'pdf')}
                style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>
                <b>Документ</b> — лише сам файл (PDF/зображення)
              </button>
              <button onClick={() => void downloadFormat(dl, 'p7s')}
                style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>
                <b>Підпис (.p7s)</b> — лише файл підпису контрагента
              </button>
              <button onClick={() => void downloadFormat(dl, 'zip')}
                style={{ textAlign: 'left', padding: '0.45rem 0.7rem' }}>
                <b>ZIP-архів</b> — документ + підписи (.p7s) відправника і
                контрагента + квитанція + інструкція перевірки
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.7rem' }}>
              <button onClick={() => setDl(null)}>закрити</button>
            </div>
          </div>
        </div>
      )}

      {signLink && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => { setSignLink(null); setLinkCopied(false); }}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8, width: 'min(560px, 94vw)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.6rem' }}>Посилання для підписання</h3>
            <input readOnly value={signLink} style={{ width: '100%' }}
              onFocus={(e) => e.target.select()} />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.6rem' }}>
              <button onClick={() => {
                navigator.clipboard.writeText(signLink)
                  .then(() => setLinkCopied(true))
                  .catch(() => setLinkCopied(false));
              }}>
                {linkCopied ? '✓ скопійовано' : 'копіювати'}
              </button>
              <button className="primary"
                onClick={() => { setSignLink(null); setLinkCopied(false); }}>
                готово
              </button>
            </div>
          </div>
        </div>
      )}

      {docView && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)',
          zIndex: 1000, display: 'flex', alignItems: 'center',
          justifyContent: 'center' }} onClick={closeDocView}>
          <div style={{ background: 'var(--bg-panel)', borderRadius: 8,
            width: 'min(920px, 94vw)', height: '90vh', display: 'flex',
            flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.35)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.6rem 0.9rem', borderBottom: '1px solid var(--border)' }}>
              <strong style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {docView.row.subject ?? 'документ'}
              </strong>
              <a href={docView.url} download={docView.row.subject ?? 'документ'}>
                <button>завантажити</button>
              </a>
              {docView.mime === 'application/pdf' && (
                <button onClick={() => viewFrameRef.current?.contentWindow?.print()}>
                  друкувати
                </button>
              )}
              <button onClick={closeDocView} title="Закрити">✕</button>
            </div>
            {docView.mime === 'application/pdf' ? (
              <iframe ref={viewFrameRef} title="Документ" src={docView.url}
                style={{ flex: 1, border: 'none', borderRadius: '0 0 8px 8px' }} />
            ) : docView.mime.startsWith('image/') ? (
              <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg-soft)',
                display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', padding: '1rem' }}>
                <img src={docView.url} alt={docView.row.subject ?? 'документ'}
                  style={{ maxWidth: '100%', height: 'auto' }} />
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: 'var(--muted)' }}>
                Попередній перегляд для цього типу файлу недоступний —
                скористайтесь «завантажити».
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/** ч.1442: документ із посилання на підпис, з якого людина пішла реєструватись. */
function PendingClaim({ onSaved }: { onSaved: () => void }) {
  const [token, setToken] = useState<string | null>(() => {
    try { return localStorage.getItem(PENDING_CLAIM_KEY); } catch { return null; }
  });
  if (!token) return null;
  const close = () => {
    try { localStorage.removeItem(PENDING_CLAIM_KEY); } catch { /* приватне вікно */ }
    setToken(null);
  };
  return (
    <div className="card esign__save" data-testid="pending-claim" style={{ marginBottom: '0.8rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
        <h3 style={{ margin: '0 0 0.5rem', flex: 1, fontSize: '1rem' }}>
          Документ за посиланням на підпис — збережіть його у журнал
        </h3>
        <button type="button" onClick={close}>не зараз</button>
      </div>
      <EdoClaimPanel token={token} onDone={onSaved} />
    </div>
  );
}
